import React from 'react';
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Heading,
  Tag,
  Text,
  FormControl,
  FormLabel,
  Image,
  Button,
} from '@chakra-ui/react';
import {
  Checkbox,
  SelectPicker,
  Toggle,
  DateRangePicker,
  Uploader,
  Button as RsButton,
} from 'rsuite';
import { FaUnlink } from 'react-icons/fa';
import qbBotton from '../../../media/images/quickbookButton.png';
import {
  checkConnection,
  objToJson,
  returnCurrencyFromCountry,
} from './integrationUtils';
import store from '../../../redux/store';
import { getAuthHeader } from '../../../utils/tokenService';

/**
 * Integration Item Component
 */
const IntegrationItem = ({
  item,
  country,
  periodTypes,
  cashAccData,
  onItemClick,
  onPeriodCalTypeChange,
  onCashAcChange,
  onEditClick,
  onBudgetSettingsClick,
  onAuthClick,
  onDisconnectClick,
  onDateRangeChange,
  onUploaderChange,
  onResyncClick,
  editButtonLoading,
  syncButtonLoading,
  periodCalTypeFlagInt,
  setPeriodCalTypeFlagInt,
  sendEditDataFlag,
  setSendEditDataFlag,
  enableBudgeting,
  yearBasedBudgeting,
  propotionalBudgeting,
  locationClassLevelBudgeting,
  limitUploader,
  excelUploadData,
  companySwitcherActive,
  apiEndpoint,
}) => {
  return (
    <AccordionItem>
      <h3>
        <AccordionButton onClick={() => onItemClick(item)}>
          <Box flex={1} textAlign={'left'} fontSize={{ base: 'xs', md: 'sm' }}>
            <Heading size={{ base: 'xs', md: 'sm' }}>{item.app_name}</Heading>
          </Box>
          <Flex
            flex={1}
            justifyContent={'flex-end'}
            gap={{ base: 2, md: 4 }}
            alignItems={'center'}
            direction={{ base: 'column', sm: 'row' }}
          >
            {item.integration_type === 'offline' ? (
              <Tag colorScheme={'yellow'} justifyContent={'center'}>
                Quickbooks Desktop
              </Tag>
            ) : (
              <Tag
                colorScheme={checkConnection(item) ? 'green' : 'red'}
                justifyContent={'center'}
              >
                {item.integration_desc}
              </Tag>
            )}
          </Flex>
          <AccordionIcon />
        </AccordionButton>
      </h3>

      <AccordionPanel flexDirection={{ base: 'column', md: 'row' }} gap={3}>
        <Flex flex={1} gap={2} p={1} direction={{ base: 'column', md: 'row' }}>
          <Flex
            flex={1}
            justifyContent={{ base: 'start', md: 'start' }}
            gap={2}
          >
            <Text size={'xs'} as={'b'}>
              Integration Type:
            </Text>
            <Text size={'xs'}>{item.integration_desc}</Text>
          </Flex>

          <Flex
            flex={1}
            justifyContent={{ base: 'start', md: 'start' }}
            gap={2}
          >
            <Text size={'xs'} as={'b'}>
              Currency:
            </Text>
            <Text size={'xs'}>
              {returnCurrencyFromCountry(item.country, country)}
            </Text>
          </Flex>

          <Flex
            flex={1}
            justifyContent={{ base: 'start', md: 'start' }}
            gap={2}
          >
            <Text size={'xs'} as={'b'}>
              Date of last sync:
            </Text>
            <Text size={'xs'}>{item.date_updated}</Text>
          </Flex>
        </Flex>

        {companySwitcherActive ? (
          <Flex
            direction={{ base: 'column', md: 'row' }}
            flex={1}
            gap={2}
            marginBottom={2}
          >
            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'center'} marginTop={2}>
                  <Text fontSize={'xs'}>Activate periods</Text>
                </FormLabel>
                <Checkbox
                  id="periodCal"
                  checked={periodCalTypeFlagInt}
                  onChange={() => {
                    setPeriodCalTypeFlagInt(!periodCalTypeFlagInt);
                    setSendEditDataFlag(!sendEditDataFlag);
                  }}
                />
              </Flex>
            </FormControl>

            <SelectPicker
              data={periodTypes}
              menuStyle={{ zIndex: 9999 }}
              loading={editButtonLoading}
              style={{ width: '100%', borderColor: 'black' }}
              disabled={!periodCalTypeFlagInt}
              onSelect={onPeriodCalTypeChange}
              placeholder={
                item.period_cal_type !== undefined
                  ? item.period_cal_type
                  : 'Select Period Calendar type.'
              }
              defaultValue={item.period_cal_type}
            />
          </Flex>
        ) : null}

        {/* Budget settings section */}
        <Box borderWidth={'1px'} marginBottom={2} padding={2} borderRadius={2}>
          <Flex direction={{ base: 'column', md: 'row' }} flex={1} gap={4}>
            <Flex align={{ base: 'center', md: 'start' }}>
              <RsButton
                color="yellow"
                appearance="primary"
                onClick={() => onBudgetSettingsClick(item.id)}
              >
                {enableBudgeting
                  ? 'Edit Budget Settings.'
                  : 'Enable Budget Settings'}
              </RsButton>
            </Flex>
            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>
                    Configure Budgeting on Location / Class Level
                  </Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  checked={locationClassLevelBudgeting}
                />
              </Flex>
            </FormControl>

            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>Inputs will be on Yearly Basis ?</Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  checked={yearBasedBudgeting}
                />
              </Flex>
            </FormControl>

            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>Divide into Equal Proportions?</Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  checked={propotionalBudgeting}
                />
              </Flex>
            </FormControl>
          </Flex>
        </Box>

        {item.integration_type === 'online' ? (
          <>
            <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
              <Flex flex={1} direction={'column'}>
                <SelectPicker
                  data={cashAccData}
                  menuStyle={{ zIndex: 9999 }}
                  style={{ width: '100%' }}
                  onChange={onCashAcChange}
                  onClean={() => onCashAcChange('')}
                  placeholder={
                    item.cash_accrual === ''
                      ? 'Select Cash or Accrual'
                      : item.cash_accrual
                  }
                  defaultValue={item.cash_accrual}
                />
              </Flex>
              <RsButton
                color="blue"
                appearance="primary"
                isLoading={editButtonLoading}
                isDisabled={!periodCalTypeFlagInt && item.cash_accrual === ''}
                onClick={onEditClick}
              >
                Edit
              </RsButton>
            </Flex>

            <Flex
              flex={1}
              justifyContent={'center'}
              gap={2}
              wrap={{ base: 'wrap', md: 'nowrap' }}
            >
              <Flex flex={1} justify={'end'} p={1}>
                <Image
                  src={qbBotton}
                  onClick={() => onAuthClick(item)}
                  style={{ cursor: 'pointer' }}
                />
              </Flex>
              <Flex flex={1} justify={'start'} p={1}>
                <RsButton
                  color="red"
                  appearance="primary"
                  isDisabled={!checkConnection(item)}
                  onClick={() => onDisconnectClick(item.id)}
                >
                  <span>
                    <FaUnlink />{' '}
                  </span>
                  <span
                    style={{
                      marginLeft: '1rem',
                      marginRight: '1rem',
                      fontSize: '1.25rem',
                    }}
                  >
                    Disconnect
                  </span>
                </RsButton>
              </Flex>
            </Flex>
          </>
        ) : item.integration_type === 'offline' ? (
          <Flex direction={'column'} gap={2} p={1}>
            <Flex gap={2} wrap={{ base: 'wrap', md: 'nowrap' }}>
              <Text size={'xs'} width={'40%'} as={'b'}>
                Date Range for data in Excel upload:
              </Text>
              <DateRangePicker
                appearance="default"
                cleanable={false}
                placeholder="Date Range"
                placement={'auto'}
                menuAutoWidth={window.screen.width > 500 ? false : true}
                style={{ width: '100%', minWidth: '200px' }}
                block
                size="sm"
                showOneCalendar
                format={store.getState().dateFormat.value}
                onOk={value => onDateRangeChange(value, item.id)}
              />
            </Flex>

            <Flex width={'100%'} justifyContent={'center'}>
              <Uploader
                listType="picture-text"
                action={`${apiEndpoint}/api/xls_fileupload_user/`}
                draggable
                autoUpload
                headers={
                  getAuthHeader() ? { Authorization: getAuthHeader() } : {}
                }
                method="POST"
                data={excelUploadData}
                multiple={false}
                accept=".xls,.csv,.xlsx"
                disabledFileItem
                disabled={
                  excelUploadData['value'] === ''
                    ? true
                    : limitUploader[item.id] === undefined
                      ? false
                      : true
                }
                onChange={filelist =>
                  onUploaderChange(filelist, item.id, limitUploader)
                }
              >
                <div
                  style={{
                    width: 500,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span>
                    Click or Drag files to this area to upload. Files will get
                    uploaded automatically
                  </span>
                </div>
              </Uploader>
            </Flex>
          </Flex>
        ) : (
          <Flex flex={1} justifyContent={'center'} gap={2} width={'100%'}>
            <RsButton
              onClick={() => onResyncClick(item)}
              color="blue"
              appearance="primary"
              style={{ width: '100%' }}
              isLoading={syncButtonLoading}
            >
              Resync/Manage connection
            </RsButton>
          </Flex>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

export default IntegrationItem;
